import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui';
import { buildIpRangeExport, type ExportFormat } from '@/lib/export/ip-range-export';
import { triggerDownload } from '@/lib/export/trigger-download';
import type { MnoIpRangeEntry, MnoOperator } from '@/types/mno-ip-range';

const FORMATS: ExportFormat[] = ['csv', 'json', 'xml'];

interface IpRangeExportMenuProps {
  entries: MnoIpRangeEntry[];
  operators: MnoOperator[];
}

/**
 * Export-Dropdown der MNO-IP-Ranges (Issue #324). Client-seitiger Datei-Download
 * in CSV / JSON / XML, identisch auf Platform- und Coverage-Seite. Die Format-
 * und Serialisierungslogik liegt im reinen Modul `@/lib/export/ip-range-export`,
 * der Download-Glue in `@/lib/export/trigger-download`.
 */
export function IpRangeExportMenu({ entries, operators }: IpRangeExportMenuProps) {
  const { t } = useTranslation('mno-ip-ranges');

  const handleExport = (format: ExportFormat) => {
    const { filename, mime, content } = buildIpRangeExport(
      format,
      entries,
      operators,
      new Date()
    );
    triggerDownload(filename, mime, content);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" />
          {t('export.button')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {FORMATS.map((format) => (
          <DropdownMenuItem key={format} onSelect={() => handleExport(format)}>
            {t(`export.${format}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
